// Day view — focused single-day grid + side summary list.

import { useEffect, useRef } from "preact/hooks";
import type { Calendar, CalendarEvent } from "../../lib/rpc";
import {
  CAL_DAYS, CAL_DAYS_LONG, allDayRange, colorFor, dateKey, daysFrom,
  eventDayHours, fmtTime, layoutOverlaps, sameDay, weekStart,
} from "../../lib/calendar-utils";
import type { EventDragController } from "../../lib/use-event-drag";
import { QueuedDot } from "../../components/queued-dot";

interface DayViewProps {
  focusDate: Date;
  setFocusDate: (d: Date) => void;
  today: Date;
  events: CalendarEvent[];
  calendars: Calendar[];
  onSelect: (evt: CalendarEvent) => void;
  onTapEmpty: (day: Date, hour: number) => void;
  drag: EventDragController;
}

// Full 24-hour window so events at any hour render at non-negative
// `top`. Auto-scroll on mount lands the user near morning.
const HOUR_START = 0;
const HOUR_END = 23;
const ROW_H = 56;
const DEFAULT_SCROLL_HOUR = 6;

export function DayView({ focusDate, setFocusDate, today, events, calendars, onSelect, onTapEmpty, drag }: DayViewProps) {
  const wkStart = weekStart(focusDate);
  const week = daysFrom(wkStart, 7);
  const hours: number[] = [];
  for (let h = HOUR_START; h <= HOUR_END; h++) hours.push(h);

  const dayEvents = events.filter((e) => {
    if (e.allDay) {
      const r = allDayRange(e);
      if (!r) return false;
      return focusDate >= r.start && focusDate <= r.end;
    }
    return eventDayHours(e, focusDate) !== null;
  });
  const allDay = dayEvents.filter((e) => e.allDay);
  const timed = dayEvents
    .filter((e) => !e.allDay)
    .map((e) => {
      const span = eventDayHours(e, focusDate)!;
      return { evt: e, start: span.start, end: span.end };
    })
    .sort((a, b) => a.start - b.start);
  const placed = layoutOverlaps(timed);
  const isFocusToday = sameDay(focusDate, today);

  // Auto-scroll to ~6 AM on every focus-date change so the user lands
  // at usable hours instead of midnight (HOUR_START is 0 to keep
  // early-morning events visible inside the grid).
  const dayScrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = dayScrollRef.current;
    if (!el) return;
    el.scrollTop = (DEFAULT_SCROLL_HOUR - HOUR_START) * ROW_H;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusDate.getTime()]);

  return (
    <div className="cal-day-wrap">
      <div className="cal-day-strip">
        {week.map((d, i) => (
          <button
            key={i}
            className="cal-day-chip"
            data-active={sameDay(d, focusDate)}
            data-today={sameDay(d, today)}
            onClick={() => setFocusDate(d)}
            type="button"
          >
            <span className="cal-day-chip-name">{CAL_DAYS[i]}</span>
            <span className="cal-day-chip-date">{d.getDate()}</span>
          </button>
        ))}
      </div>

      <div className="cal-day-body">
        <div className="cal-day-scroll" ref={dayScrollRef}>
          {allDay.length > 0 && (
            <div className="cal-day-allday">
              <div className="eyebrow" style={{ marginBottom: 6 }}>All day</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {allDay.map((e) => (
                  <div
                    key={e.id}
                    className="cal-allday-evt"
                    data-dragging={drag.draggingId === e.id}
                    onPointerDown={drag.onPointerDown(e)}
                    style={{ ["--evt" as any]: `var(--${colorFor(e, calendars)})` }}
                    onClick={() => onSelect(e)}
                  >
                    <span className="cal-allday-dot" />
                    <span className="cal-allday-title">
                      {e.title}
                      <QueuedDot queued={e._queued} />
                    </span>
                    {e.location && <span className="cal-allday-loc">· {e.location}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="cal-day-grid">
            <div className="cal-time-col">
              {hours.map((h) => (
                <div key={h} className="cal-time-row" style={{ height: ROW_H }}>
                  <span>{fmtTime(h)}</span>
                </div>
              ))}
            </div>
            <div
              className="cal-day-col"
              data-today={isFocusToday}
              data-drop-hour-col={dateKey(focusDate)}
              data-drop-start-hour={HOUR_START}
              data-drop-row-h={ROW_H}
            >
              {hours.map((h) => (
                <div
                  key={h}
                  className="cal-hour-cell"
                  style={{ height: ROW_H }}
                  onClick={() => onTapEmpty(focusDate, h)}
                />
              ))}
              {placed.map(({ evt, col, cols }) => {
                const top = (evt.start - HOUR_START) * ROW_H;
                const height = (evt.end - evt.start) * ROW_H - 4;
                const widthPct = 100 / cols;
                return (
                  <div
                    key={evt.evt.id}
                    className="cal-evt cal-evt-lg"
                    data-dragging={drag.draggingId === evt.evt.id}
                    onPointerDown={drag.onPointerDown(evt.evt)}
                    style={{
                      top, height,
                      left: `calc(${col * widthPct}% + 4px)`,
                      width: `calc(${widthPct}% - 8px)`,
                      ["--evt" as any]: `var(--${colorFor(evt.evt, calendars)})`,
                    }}
                    onClick={(e) => { e.stopPropagation(); onSelect(evt.evt); }}
                  >
                    <div className="cal-evt-title">
                      {evt.evt.title}
                      <QueuedDot queued={evt.evt._queued} />
                    </div>
                    <div className="cal-evt-time">{fmtTime(evt.start)} – {fmtTime(evt.end)}</div>
                    {evt.evt.location && <div className="cal-evt-loc">{evt.evt.location}</div>}
                  </div>
                );
              })}
              {isFocusToday && <NowLine startHour={HOUR_START} rowH={ROW_H} />}
            </div>
          </div>
        </div>

        <aside className="cal-day-side">
          <div className="eyebrow">{dayEvents.length} {dayEvents.length === 1 ? "thing" : "things"}</div>
          <div className="cal-day-side-headline">
            {isFocusToday
              ? "Today's shape, at a glance."
              : `What ${CAL_DAYS_LONG[focusDate.getDay()]} looks like.`}
          </div>
          <div style={{ marginTop: 18, display: "flex", flexDirection: "column", gap: 6 }}>
            {dayEvents.length === 0 && (
              <div style={{ color: "var(--text-dim)", fontSize: 13.5 }}>Nothing planned. Lovely.</div>
            )}
            {dayEvents.map((e) => {
              const range = e.allDay ? null : eventDayHours(e, focusDate);
              return (
                <button key={e.id} className="cal-day-side-row" onClick={() => onSelect(e)} type="button">
                  <span className="cal-day-side-bar" style={{ background: `var(--${colorFor(e, calendars)})` }} />
                  <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>{e.title}</div>
                    <div style={{ fontSize: 11, color: "var(--text-dim)", fontFamily: "var(--f-mono)" }}>
                      {e.allDay ? "all day" : range ? `${fmtTime(range.start)} – ${fmtTime(range.end)}` : ""}
                      {e.location && ` · ${e.location}`}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </aside>
      </div>
    </div>
  );
}

function NowLine({ startHour, rowH }: { startHour: number; rowH: number }) {
  const now = new Date();
  const hour = now.getHours() + now.getMinutes() / 60;
  if (hour < startHour || hour > HOUR_END + 1) return null;
  const top = (hour - startHour) * rowH;
  return <div className="cal-now-line" style={{ top }} />;
}
